/*
 * The navigation drawer: 26 modules in shop-floor order, each with the
 * sub-pages that live under it.  Sub-pages are not listed by default (that is
 * the simplification) but they are searchable, and the active module reveals
 * its own sub-pages beneath it.
 *
 * `module` is the permission module slug the role needs (see backend/seed.py
 * modules_data); null means every logged-in user.  Only modules whose routes the
 * server itself gates on that slug are gated here - a menu stricter than the API
 * would hide screens from roles that can use them (the seeded HOD QAS, Planner,
 * Warehouse and Dispatch roles start with no permissions at all).  `match`
 * lists extra URL prefixes the entry owns, for breadcrumbs and access checks.
 * `abbr` is the document code people type for that screen (GRN, PRC, CIOA, DC)
 * and wins the search outright; `keywords` are softer aliases: old menu labels
 * and plant vocabulary.
 */
import {
  House, Users, Shield, Cube, Storefront, ClipboardText, TrayArrowDown, CheckCircle,
  Warehouse, Prohibit, TrayArrowUp, Flame, Path, Factory, Table, Flask, TestTube,
  Archive, Package, Truck, ChartBar, MagnifyingGlass, DownloadSimple, TrendUp,
  ClockCounterClockwise, Gear,
} from '@phosphor-icons/react';

export const GROUP_LABELS = {
  overview: null,
  setup: 'Setup',
  sales: 'Sales',
  inward: 'Inward',
  stores: 'Stores',
  production: 'Production',
  quality: 'Quality',
  dispatch: 'Dispatch',
  insights: 'Insights',
  system: 'System',
};

export const NAV_MODULES = [
  { key: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: House, module: null, group: 'overview',
    keywords: ['home', 'overview', 'kpi', 'summary'] },

  { key: 'users', path: '/users', label: 'Users', icon: Users, module: 'user_management', group: 'setup',
    keywords: ['accounts', 'login', 'employees', 'staff'],
    children: [{ path: '/users/new', label: 'New User' }] },
  { key: 'roles', path: '/roles', label: 'Role & Permissions', icon: Shield, abbr: ['rbac'], module: 'user_management', group: 'setup',
    keywords: ['roles', 'access', 'rights', 'rbac', 'permission studio'],
    children: [{ path: '/roles/compare', label: 'Compare Roles' }] },
  { key: 'masters', path: '/masters', label: 'Masters', icon: Cube, module: 'master_management', group: 'setup',
    keywords: ['master data', 'setup', 'reference data'],
    children: [
      { path: '/masters/rm_master', label: 'Raw Material Master', keywords: ['rm master', 'rm'] },
      { path: '/masters/fg', label: 'Finished Goods Master', keywords: ['fg master', 'fg code'], match: ['/masters/fg_master', '/fg-master'] },
      { path: '/masters/suppliers', label: 'Suppliers', keywords: ['vendors'] },
      { path: '/masters/customers', label: 'Customers' },
      { path: '/masters/transporters', label: 'Transporters / Vehicles' },
      { path: '/masters/grades', label: 'Grades', keywords: ['chemistry'] },
      { path: '/masters/uom', label: 'Units of Measure', keywords: ['uom'] },
      { path: '/masters/process-standard', label: 'Process Standard Master', keywords: ['pass sequence'] },
      { path: '/masters/process-route', label: 'Process Route Prep', keywords: ['passes', 'reduction'] },
      { path: '/masters/wire-shapes', label: 'Wire Shape Master', keywords: ['shape', 'axes', 'formula'] },
      { path: '/masters/shape-transformations', label: 'Shape Transformation Master' },
      { path: '/masters/warehouses', label: 'Warehouses / Bins', keywords: ['bay', 'bin', 'location'] },
      { path: '/masters/furnaces', label: 'Furnace Master' },
      { path: '/masters/annealing_cycles', label: 'Annealing Cycle Master', keywords: ['annealing cycle', 'cycle', 'soaking', 'temperature'] },
      { path: '/masters/nc_codes', label: 'NC Codes', keywords: ['non conformance codes'] },
      { path: '/masters/units', label: 'Units / Plants' },
      { path: '/masters/companies', label: 'Companies' },
    ] },

  { key: 'sales', path: '/sales', label: 'Sales & Marketing', icon: Storefront, module: null, group: 'sales',
    keywords: ['marketing', 'customer', 'mkt', 'orders'],
    children: [
      { path: '/sales/inquiries', label: 'Customer Inquiries', abbr: ['cii'], keywords: ['enquiry', 'inquiry'] },
      { path: '/sales/inquiries/dashboard', label: 'Inquiry Dashboard' },
      { path: '/sales/inquiries/register', label: 'Inquiry Register' },
      { path: '/sales/feasibility', label: 'Feasibility Reports', abbr: ['fsr'], keywords: ['study'] },
      { path: '/sales/costing', label: 'Cost Sheets', keywords: ['costing', 'price'], match: ['/sales/cost-sheets'] },
      { path: '/sales/quotations', label: 'Quotations', keywords: ['quote'] },
      { path: '/sales/customer-po', label: 'Customer PO', abbr: ['cpo'], keywords: ['customer purchase order'] },
      { path: '/sales/sales-orders', label: 'Sales Orders', abbr: ['so', 'cioa'], keywords: ['order'] },
      { path: '/sales/pre-dispatch', label: 'Pre-Dispatch Advice', abbr: ['pda'] },
      { path: '/sales/payments', label: 'Payment Collections', keywords: ['bills', 'receivables'] },
      { path: '/customer-care/complaints', label: 'Customer Complaints', abbr: ['ccr'], keywords: ['complaint', 'customer care'] },
    ] },

  { key: 'purchase-orders', path: '/purchase-orders', label: 'Purchase Order', icon: ClipboardText, module: 'purchase_order', group: 'inward',
    abbr: ['po'], keywords: ['procurement', 'supplier order', 'buying'],
    children: [{ path: '/purchase-orders/new', label: 'New Purchase Order' }] },
  { key: 'rm-inward', path: '/grn', label: 'RM Inward', icon: TrayArrowDown, module: 'rm_inward', group: 'inward',
    abbr: ['grn'], keywords: ['goods receipt', 'receipt', 'inward note', 'coils', 'heat', 'raw material inward'],
    children: [{ path: '/grn/new', label: 'New GRN' }] },
  { key: 'inspection', path: '/inspection', label: 'Inspection', icon: CheckCircle, module: 'inspection', group: 'inward',
    keywords: ['gate', 'inward inspection', 'qa', 'release', 'r-03', 'available for rm issue', 'quality gate'] },
  { key: 'storage', path: '/storage', label: 'Storage & Binning', icon: Warehouse, module: 'storage_binning', group: 'inward',
    keywords: ['bins', 'bays', 'putaway', 'stock', 'warehouse', 'store'] },
  { key: 'blocked-stock', path: '/blocked-stock', label: 'Blocked Stock', icon: Prohibit, module: 'inspection', group: 'inward',
    abbr: ['blk'], keywords: ['rejected', 'hold', 'deviation', 'unblock', 'scrap', 'quarantine'] },

  { key: 'rm-outward', path: '/rm-outward', label: 'RM Outward', icon: TrayArrowUp, module: 'rm_outward', group: 'stores',
    keywords: ['return to supplier', 'outward', 'internal shift', 'raw material outward'],
    children: [{ path: '/rm-outward/new', label: 'New RM Outward' }] },
  { key: 'rm-issue', path: '/rm-issue', label: 'RM Issue', icon: Flame, module: 'rm_issue', group: 'stores',
    abbr: ['rmi'], keywords: ['issue', 'dc', 'charge', 'furnace', 'material issue', 'raw material issue'],
    children: [
      { path: '/rm-issue/new', label: 'New RM Issue' },
      { path: '/job-work', label: 'Job Work', abbr: ['jw'], keywords: ['vendor', 'external', 'subcontract'] },
      // Job Work flow: post-annealing inspection -> SFG Register -> JW PDA -> JW Delivery Challan.
      { path: '/job-work/sfg', label: 'Job Work Inspection / SFG', abbr: ['sfg'], keywords: ['semi finished', 'sfg register', 'post annealing inspection', 'job work inspection', 'unit 3'] },
      { path: '/job-work/pda', label: 'Job Work PDAs', keywords: ['jw pda', 'pre dispatch advice', 'job work challan'] },
    ] },

  { key: 'production-planning', path: '/production/prc', label: 'Production Planning', icon: Path, module: 'production_planning', group: 'production',
    keywords: ['plan', 'planning run', 'fg', 'sales order', 'schedule'],
    children: [
      // The client's order: PRC master, then allot SC on it, then plan.
      { path: '/prc/new', label: '1. New Process Route Card' },
      { path: '/prc', label: '2. Process Route Cards · allot SC', abbr: ['prc'], keywords: ['route card', 'design', 'sc allotment', 'stock code allotment'] },
      { path: '/production/prc/new', label: '3. New Production Plan (on a PRC)' },
    ] },
  { key: 'production', path: '/production', label: 'Production', icon: Factory, module: 'production_planning', group: 'production',
    keywords: ['shop floor', 'execution', 'coil', 'annealing', 'nc'],
    children: [
      { path: '/production/execution', label: 'Production Execution', keywords: ['per coil', 'monitoring', 'coil entry'] },
      { path: '/production/annealing-plans', label: 'Annealing Plans', keywords: ['furnace', 'spheroidise', 'charge'], match: ['/annealing', '/production/annealing-schedules'] },
      { path: '/production/non-conformance', label: 'Non-Conformance', abbr: ['nc', 'ncr'], keywords: ['rework', 'scrap'] },
      { path: '/production/trail-sheets', label: 'Trail Process Sheets' },
    ] },
  { key: 'production-data', path: '/production-data', label: 'Production Data', icon: Table, module: null, group: 'production',
    keywords: ['daily production', 'shift', 'dies', 'rollers', 'mill tc', 'data entry'],
    children: [
      { path: '/production-data/daily-production', label: 'Daily Production' },
      { path: '/production-data/section-production', label: 'Section Production' },
      { path: '/production-data/dies', label: 'Dies Register', keywords: ['die'] },
      { path: '/production-data/rollers', label: 'Rollers Register' },
      { path: '/production-data/annealing', label: 'Annealing Log' },
      { path: '/production-data/mill-tc', label: 'Mill Test Certificates', keywords: ['mtc', 'mill tc'] },
    ] },

  { key: 'lab-quality', path: '/lab-quality', label: 'Lab Quality', icon: Flask, module: null, group: 'quality',
    keywords: ['lab', 'certificates', 'qas', 'test', 'laboratory'],
    children: [
      { path: '/lab-quality/rm-inspection', label: 'RM Inward Inspection (R-03)', keywords: ['r-03', 'r03'] },
      { path: '/lab-quality/furnace-inspection', label: 'Furnace Inspection' },
      { path: '/lab-quality/micro-hardness', label: 'Micro & Hardness' },
      { path: '/lab-quality/mechanical-properties', label: 'Mechanical Properties', keywords: ['uts', 'elongation'] },
      { path: '/lab-quality/test-certificates', label: 'Final Test Certificates', abbr: ['tc', 'mtc'], keywords: ['certificate'], match: ['/lab-quality/test-certificate'] },
      { path: '/lab-quality/nc-register', label: 'NC Register', keywords: ['non conformance'] },
      { path: '/lab-quality/traceability', label: 'Heat Traceability', keywords: ['heat code', 'trace'], match: ['/trace/heat'] },
    ] },

  // One FG Inspection, under Quality where the inspectors are: the queue of
  // coils completed in annealing, OK / NG / Hold with the test results.  FG
  // Testing is folded in (its old register is linked from the page), and the
  // Production sub-item that pointed at a blank record form is gone.
  { key: 'fg-inspection', path: '/production/fg-inspection', label: 'FG Inspection', icon: TestTube, module: null, group: 'quality',
    keywords: ['ok ng hold', 'fg testing', 'fg test', 'final testing', 'uts', 'hardness', 'finished goods inspection'] },

  { key: 'finished-goods', path: '/finished-goods/inventory', label: 'Finished Goods', icon: Archive, module: null, group: 'dispatch',
    abbr: ['fg'], keywords: ['inventory', 'stock', 'fg stock'], match: ['/finished-goods'] },
  { key: 'ready-to-dispatch', path: '/dispatch/ready', label: 'Ready to Dispatch', icon: Package, module: 'dispatch', group: 'dispatch',
    keywords: ['ready', 'dispatchable', 'awaiting dispatch'] },
  { key: 'dispatch-challans', path: '/dispatch', label: 'Dispatch Challans', icon: Truck, module: 'dispatch', group: 'dispatch',
    abbr: ['dc'], keywords: ['challan', 'delivery', 'invoice', 'shipping'],
    children: [{ path: '/dispatch/new', label: 'New Dispatch Challan' }] },

  { key: 'reports', path: '/reports', label: 'Reports', icon: ChartBar, module: null, group: 'insights',
    keywords: ['registers', 'excel', 'print', 'mis'] },
  { key: 'stock-code-trace', path: '/trace/stock-code', label: 'Stock Code Trace', icon: MagnifyingGlass, module: null, group: 'insights',
    abbr: ['sc'], keywords: ['trace', 'traceability', 'stock code'], match: ['/trace'],
    children: [{ path: '/trace/prc', label: 'PRC 360', keywords: ['prc trace'] }] },
  { key: 'exports', path: '/exports', label: 'Export Hub', icon: DownloadSimple, module: null, group: 'insights',
    keywords: ['excel', 'pdf', 'download', 'formats', 'templates', 'exports hub'] },
  { key: 'vendor-analysis', path: '/analytics/vendor-rating', label: 'Vendor Analysis', icon: TrendUp, module: null, group: 'insights',
    keywords: ['supplier rating', 'vendor rating', 'performance', 'vendor analytics'],
    children: [{ path: '/operations/supplier-evaluation', label: 'Supplier Evaluation' }] },

  { key: 'audit-log', path: '/audit-logs', label: 'Audit Log', icon: ClockCounterClockwise, module: 'user_management', group: 'system',
    keywords: ['history', 'who did what', 'activity', 'audit logs'] },
  { key: 'settings', path: '/settings', label: 'Settings', icon: Gear, module: 'user_management', adminOnly: true, group: 'system',
    keywords: ['logo', 'document numbers', 'configuration', 'preferences'] },
];
